/**
 * TestimonialsSection.jsx — client testimonials (V2)
 * ──────────────────────────────────────────────────
 * `TESTIMONIALS` is the shared data contract (the /business page imports it):
 * keep its name, shape and values. Quotes are verbatim.
 *
 * The default export renders the V2 quote cards of the approved landing
 * (`.lp-quotes` > `figure.tq`: quote icon, quote, photo, name, title,
 * organisation on its own line, organisation logo). It is self-contained: its
 * styles (src/v2/landing/quotes.css, every rule under `.lp-quotes`) are imported
 * here, so it renders the same on any page. Wrap it with the page's own
 * section heading.
 */

import React from 'react';
import { QuoteUpIcon } from '@hugeicons/core-free-icons';
import { useLocale } from '../lib/i18n';
import Icon from './v2/Icon.jsx';
import '../v2/landing/quotes.css';

export const TESTIMONIALS = [
  {
    id: 'yasser-waad',
    name: ['ياسر الغامدي', 'Yasser Alghamdi'],
    title: ['CIO والمؤسس', 'CIO & Founder'],
    org: ['Waad Investment', 'Waad Investment'],
    photo: '/testimonials/yasser-alghamdi.jpg',
    logo: '/testimonials/waad-investment.png',
    quote: [
      'استخدمت بطاقات العمل الرقمية CardPass، الخدمات الي تقدمها جداً ممتازه.. شكراً لمحمد وفريق CardPass.',
      'I used CardPass digital business cards — the services are truly excellent. Thank you Mohammed and the CardPass team.'
    ]
  },
  {
    id: 'abdullah-pra',
    name: ['عبدالله العقلا', 'Abdullah Alogla'],
    title: ['رئيس مجلس الإدارة', 'Chairman of the Board'],
    org: ['جمعية العلاقات العامة', 'Public Relations Association'],
    photo: '/testimonials/abdullah-alogla.jpg',
    logo: '/testimonials/pra-logo.png',
    quote: [
      'كارد باس مشروع له احتياج حقيقي، ويساهم بشكل مباشر في التحول الرقمي والاستغناء عن البطاقات الورقية التقليدية. مستوى التقدم والتطوير الذي أشاهده في المشروع ينبئ بمستقبل باهر بإذن الله.',
      'CardPass answers a real need — it directly advances digital transformation and replaces traditional paper cards. The pace of progress I see in this project promises a bright future, God willing.'
    ]
  }
];

const LATIN = /^[\x20-\x7E]+$/;

/** Isolate Latin runs inside Arabic text (e.g. «CIO والمؤسس») so bidi never flips them. */
function bidiRuns(text, ar) {
  if (!ar) return text;
  return text.split(/([A-Za-z][A-Za-z0-9 .&-]*[A-Za-z0-9])/).map((part, i) =>
    (i % 2 === 1 ? <span key={i} className="ltr">{part}</span> : part));
}

function TestimonialCard({ item }) {
  const ar = useLocale() === 'ar';
  const pick = (v) => (ar ? v[0] : v[1]);
  const org = pick(item.org);
  const orgLatin = LATIN.test(org);

  return (
    <figure className="tq">
      <Icon icon={QuoteUpIcon} className="qi" />
      <blockquote>{pick(item.quote)}</blockquote>
      <figcaption>
        <img className="p" src={item.photo} alt={pick(item.name)} loading="lazy" />
        <div className="who">
          <b>{pick(item.name)}</b>
          <span>{bidiRuns(pick(item.title), ar)}</span>
          <span className={orgLatin && ar ? 'ltr vr' : undefined}>{org}</span>
        </div>
        <span className="olg"><img src={item.logo} alt={org} loading="lazy" /></span>
      </figcaption>
    </figure>
  );
}

/** The quote cards (2 columns, 1 at ≤980px). Wrap it with the page's own section heading. */
export default function TestimonialCards() {
  const locale = useLocale();
  return (
    <div className="lp-quotes" data-lang={locale}>
      {TESTIMONIALS.map((item) => (
        <TestimonialCard key={item.id} item={item} />
      ))}
    </div>
  );
}
